export type ChartValue = number | "-";

export interface OverlappedBarInput {
    name: string;
    color: string;
    values: ChartValue[];
}

export interface OverlappedBarSeries {
    name: string;
    type: "bar";
    barGap: string;
    z: number;
    itemStyle: { color: string; borderRadius: number[] };
    data: ChartValue[];
}

export interface AxisTooltipEntry {
    name: string;
    unit: string;
}

export interface AxisTooltipParam {
    seriesName?: string;
    value?: unknown;
    marker?: unknown;
    axisValueLabel?: string;
}

const BAR_BORDER_RADIUS = [12, 12, 0, 0];
const BASE_Z = 2;

const hasValue = (value: unknown): value is number => typeof value === "number" && !Number.isNaN(value);

/**
 * Monta séries de barra sobrepostas (mesma posição no eixo X) com a ordem de sobreposição calculada
 * POR PERÍODO: em cada coluna a maior fica ao fundo e as demais à frente em ordem decrescente. SGD-3827.
 *
 * No ECharts a ordem de desenho (z) é por série, não por ponto. Por isso cada entrada vira N camadas
 * (N = número de entradas): a camada 0 recebe, em cada período, só o valor de maior rank; a camada N-1,
 * o de menor. As camadas de uma mesma entrada compartilham `name` e cor, então a legenda mostra um item
 * por entrada e ocultá-lo esconde todas as suas camadas.
 *
 * Empate: valores iguais no mesmo período ficam com a mesma altura e a barra da frente cobre a de trás
 * (a ordem segue a de `inputs`). O valor de ambas continua no tooltip. Comportamento aceito por ora —
 * a validar com tech lead/design (alternativa: larguras diferentes por camada).
 */
export function buildOverlappedBarSeries(inputs: OverlappedBarInput[]): OverlappedBarSeries[] {
    const layers = inputs.length;
    const periods = Math.max(0, ...inputs.map((input) => input.values.length));
    const layerValues: ChartValue[][][] = inputs.map(() =>
        Array.from({length: layers}, () => Array<ChartValue>(periods).fill("-"))
    );

    for (let period = 0; period < periods; period++) {
        inputs
            .map((input, index) => ({index, value: input.values[period]}))
            .filter((entry): entry is { index: number; value: number } => hasValue(entry.value))
            .sort((a, b) => b.value - a.value)
            .forEach((entry, rank) => {
                layerValues[entry.index][rank][period] = entry.value;
            });
    }

    const series: OverlappedBarSeries[] = [];
    for (let layer = 0; layer < layers; layer++) {
        inputs.forEach((input, index) => {
            series.push({
                name: input.name,
                type: "bar",
                barGap: "-100%",
                z: BASE_Z + layer,
                itemStyle: {color: input.color, borderRadius: BAR_BORDER_RADIUS},
                data: layerValues[index][layer],
            });
        });
    }
    return series;
}

// Mesmo markup do tooltip padrão do ECharts 5 (nome à esquerda, valor em negrito à direita), sem fixar
// cor para herdar a do tema (claro/escuro).
const TOOLTIP_ROW_GAP = 10;
const TOOLTIP_NAME_STYLE = "font-size:14px;font-weight:400";
const TOOLTIP_VALUE_STYLE = "float:right;margin-left:20px;font-size:14px;font-weight:900";
const CLEARFIX = `<div style="clear:both"></div>`;

const tooltipRowHtml = (marker: string, name: string, value: string, topGap: number): string =>
    `<div style="margin: ${topGap}px 0 0;line-height:1;">`
    + `${marker}<span style="${TOOLTIP_NAME_STYLE};margin-left:2px">${name}</span>`
    + `<span style="${TOOLTIP_VALUE_STYLE}">${value}</span>`
    + `${CLEARFIX}</div>`;

/**
 * Formatter de tooltip (trigger 'axis') que junta as camadas de {@link buildOverlappedBarSeries} em uma
 * linha por série, na ordem de `entries`, com '-' quando não há valor no período. Séries ocultas pela
 * legenda não chegam em `params` e por isso não são listadas.
 */
export function buildAxisTooltipFormatter(entries: AxisTooltipEntry[]) {
    return (rawParams: AxisTooltipParam | AxisTooltipParam[]): string => {
        const params = Array.isArray(rawParams) ? rawParams : [rawParams];
        const byName = new Map<string, { marker: string; value: number | null }>();

        params.forEach((param) => {
            if (!param.seriesName) {
                return;
            }
            const current = byName.get(param.seriesName);
            const value = hasValue(param.value) ? param.value : null;
            if (!current) {
                byName.set(param.seriesName, {marker: typeof param.marker === "string" ? param.marker : "", value});
            } else if (current.value === null && value !== null) {
                current.value = value;
            }
        });

        const rows = entries
            .filter((entry) => byName.has(entry.name))
            .map((entry, index) => {
                const {marker, value} = byName.get(entry.name)!;
                return tooltipRowHtml(marker, entry.name, value === null ? "-" : `${value} ${entry.unit}`, index === 0 ? 0 : TOOLTIP_ROW_GAP);
            });

        return `<div style="margin: 0px 0 0;line-height:1;">`
            + `<div style="${TOOLTIP_NAME_STYLE};line-height:1;">${params[0]?.axisValueLabel ?? ""}</div>`
            + `<div style="margin: ${TOOLTIP_ROW_GAP}px 0 0;line-height:1;">${rows.join("")}${CLEARFIX}</div>`
            + `${CLEARFIX}</div>`;
    };
}
